import { AutoMappingRule, ModelCommandType, ProfileConfigurationResponse } from "@api/apiClient";
import DataGrid from "@components/DataGrid/DataGrid";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { ProfileSettingsGrid } from "./ProfileSettingsGrid";
import { GridColDef, GridToolbar } from "@mui/x-data-grid-premium";
import React, { useEffect, useState } from "react";
import { useCadModelCategories } from "@api/cadModelCategories";
import { useCo2MaterialTypes } from "@api/co2MaterialTypes";
import { filterRowTreeFromGroupingColumns } from "@mui/x-data-grid-premium/hooks/features/rowGrouping/gridRowGroupingUtils";
import i18n from "@i18n/i18n";
// Type definitions
export class ProfileResponseModel {
  srNo: number | undefined;
  id?: number;
  profileId?: string;
  name?: string;
  description?: string;
  deleteStatus?: string;
  isDeleted?: boolean;
  rebarEnabled?: boolean;
  profileConfigurationResponse?: ProfileConfigurationResponse;
}

export interface ProfileDetailPanelProps {
  row: ProfileResponseModel;
}

export interface CustomTabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

export interface GridRowData {
  id: string;
  category: string;
  family: string;
  type: string;
  percentage: number;
  ecMaterial: string;
  system: string;
  subsystem: string;
  Attribute1: string;
  Attribute2: string;
  Attribute3: string;
}

// Custom TabPanel component
function CustomTabPanel(props: CustomTabPanelProps): JSX.Element {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`profile-detail-tabpanel-${index}`} aria-labelledby={`profile-detail-tab-${index}`}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

// Common grid columns configuration
const getCommonGridColumns = (cadModelCategories: ReturnType<typeof useCadModelCategories>, co2MaterialTypes: ReturnType<typeof useCo2MaterialTypes>): GridColDef[] => [
  {
    field: "category",
    headerName: i18n.t("profile-import.settings-grid.columns.category"),
    flex: 1,
    editable: false,
    valueGetter: (row) => {
      console.info("Getting category for row:", row);
      const categoryId = row;
      const category = cadModelCategories.data?.find((cat) => cat.cadModelCategoryId === categoryId);
      return category?.name || categoryId || "N/A";
    },
  },
  {
    field: "family",
   headerName: i18n.t("profile-import.settings-grid.columns.family"),
    flex: 1,
    editable: false,
  },
  {
    field: "type",
    headerName: i18n.t("profile-import.settings-grid.columns.type"),
    flex: 1,
    editable: false,
  },
  {
    field: "ecMaterial",
    headerName: i18n.t("profile-import.settings-grid.columns.ecmaterial"),
    flex: 1,
    editable: false,
    valueGetter: (row) => {
      console.info("Getting category for row:", row);
      const categoryId = row;
      const category = co2MaterialTypes.data?.find((cat) => cat.co2MaterialTypeId === categoryId);
      return category?.name || categoryId || "N/A";
    },
  },
  {
    field: "system",
    headerName: i18n.t("profile-import.settings-grid.columns.system"),
    flex: 1,
    editable: false,
  },
  {
    field: "subsystem",
    headerName: i18n.t("profile-import.settings-grid.columns.sub-system"),
    flex: 1,
    editable: false,
  },
  {
    field: "Attribute1",
    headerName: i18n.t("profile-import.settings-grid.columns.attribute-1"),
    flex: 1,
    editable: false,
  },
  {
    field: "Attribute2",
    headerName: i18n.t("profile-import.settings-grid.columns.attribute-2"),
    flex: 1,
    editable: false,
  },
  {
    field: "Attribute3",
    headerName: i18n.t("profile-import.settings-grid.columns.attribute-3"),
    flex: 1,
    editable: false,
  },
];

// Main ProfileDetailPanel component
export const ProfileDetailPanel: React.FC<ProfileDetailPanelProps> = ({ row }) => {
  const [tabValue, setTabValue] = useState<number>(0);
  const cadModelCategories = useCadModelCategories();
  const co2MaterialTypes = useCo2MaterialTypes();
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number): void => {
    setTabValue(newValue);
  };
  useEffect(() => {
    console.info("ProfileDetailPanel row prop:", cadModelCategories.data);
  }, [cadModelCategories.data]);
  //const commonColumns = getCommonGridColumns(cadModelCategories, co2MaterialTypes);
  const getColumnsByTab = (tabIndex: number): GridColDef[] => {
    console.info("getColumnsByTab called for tab:", tabIndex);
    
    var commonColumns = getCommonGridColumns(cadModelCategories, co2MaterialTypes);
    switch (tabIndex) { 
      case 0:
        return commonColumns; 
      case 1:
      case 2:
        // Insert percentage column after "type" column (index 2) and before "ecMaterial" column
        const percentageColumn: GridColDef = {
          field: "percentage",
          headerName: i18n.t("profile-import.settings-grid.columns.percentage"),
          flex: 0.5,
          type: "number",
          editable: true,
        };
        
        commonColumns.splice(3, 0, percentageColumn);        
        return commonColumns; 
      default:
        return commonColumns;
    }
  }

  // Generate columns only for the active tab to avoid unnecessary calls
  const currentColumns = getColumnsByTab(tabValue);
  // Handle row updates 
  const handleRowUpdate = (updatedRow: GridRowData, originalRow: GridRowData) => {
    console.info("Row updated:", { updatedRow, originalRow });
    // Here you can add logic to save the updated data
    // For example, call an API to update the percentage value
  };

  // Transform automapping rules data for grid display
  const getSettingsData = (filterType: ModelCommandType): GridRowData[] => {
    const settingsData = (() => {
      //  const config = row?.profileConfigurationResponse;
      let config = (row as any)["ProfileConfigurationResponse"];
      if (config == undefined) {
        config = (row as any)["profileConfigurationResponse"];
      }
      if (!config) return [];

      switch (filterType) {
        case ModelCommandType.AutoAssignMaterial:
          return config.autoMappingRules || [];
        case ModelCommandType.AddNonModelledElement:
          return config.nonModelledRules || [];
        case ModelCommandType.AssignRebarEstimation:
          return config.rebarRules || [];
        default:
          return [];
      }
    })();
    if (!settingsData || settingsData.length === 0) {
      return [];
    }

    return settingsData
      .filter((rule: AutoMappingRule) => {
        // Filter based on tab type if needed
        // For AutoMapping tab, show all rules
        // For Non Modelled and Rebar, you can add specific filtering logic
        return rule.cadModelCategory || rule.family || rule.type;
      })
      .map(
        (rule: AutoMappingRule, index: number): GridRowData => ({
          id: `${filterType}-${index}`,
          category: rule.cadModelCategory || "N/A",
          family: rule.family || "N/A",
          type: rule.type || filterType,
          percentage: Number(rule.percentage) ?? 100, // Default percentage, can be derived from rule data if available
          ecMaterial: rule.co2MaterialTypeId || rule.system || "N/A",
          system: rule.system as string,
          subsystem: rule.subSystem as string ,
          Attribute1: rule.attribute1 as string,
          Attribute2: rule.attribute2 as string ,
          Attribute3: rule.attribute3 as string ,
        }),
      );
  };

  return (
    <Box sx={{ width: "100%", bgcolor: "background.paper" }}>
      <Tabs value={tabValue} onChange={handleTabChange} aria-label="profile detail tabs">
        <Tab label="AutoMapping" id="profile-detail-tab-0" aria-controls="profile-detail-tabpanel-0" />
        <Tab label="Non Modelled" id="profile-detail-tab-1" aria-controls="profile-detail-tabpanel-1" />
        <Tab label="Rebar" id="profile-detail-tab-2" aria-controls="profile-detail-tabpanel-2" />
      </Tabs>

      {tabValue === 0 && (
        <CustomTabPanel value={tabValue} index={0}>
          <ProfileSettingsGrid
            title="AutoMapping Configuration"
            columns={currentColumns}
            rows={getSettingsData(ModelCommandType.AutoAssignMaterial)}
            showToolbar={true}
            onRowUpdate={handleRowUpdate}
          />
        </CustomTabPanel>
      )}

      {tabValue === 1 && (
        <CustomTabPanel value={tabValue} index={1}>
          <ProfileSettingsGrid
            showToolbar={true}
            title="Non Modelled Elements"
            columns={currentColumns}
            rows={getSettingsData(ModelCommandType.AddNonModelledElement)}
            onRowUpdate={handleRowUpdate}
          />
        </CustomTabPanel>
      )}

      {tabValue === 2 && (
        <CustomTabPanel value={tabValue} index={2}>
          <ProfileSettingsGrid
            showToolbar={true}
            title="Rebar Configuration"
            columns={currentColumns}
            rows={getSettingsData(ModelCommandType.AssignRebarEstimation)}
            onRowUpdate={handleRowUpdate}
          />
        </CustomTabPanel>
      )}
    </Box>
  );
};

// Detail panel content function
export const getDetailPanelContent = ({ row }: { row: ProfileResponseModel }): JSX.Element => {
  console.info("Detail panel row data:", row);
  return <ProfileDetailPanel row={row} />;
};

export default ProfileDetailPanel;
